/**
 * Search Button for Consumer Portal Command Palette
 *
 * A header button that triggers the command palette.
 * Simplified design for consumer-facing portal.
 */

import SearchIcon from '@mui/icons-material/Search';
import { alpha, Box, fontSizes, iconSizes, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { useCommandPaletteContext } from './CommandPaletteProvider';

export function SearchButton(): React.ReactElement {
  const { open } = useCommandPaletteContext();
  const { t } = useI18n();
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  return (
    <Box
      onClick={open}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      aria-label={t('consumer.search.openSearch')}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1.5,
        borderRadius: 1,
        bgcolor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.05)
            : alpha(theme.palette.common.black, 0.04),
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        minWidth: 540,
        transition: 'all 150ms ease',
        '&:hover': {
          bgcolor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.08)
              : alpha(theme.palette.common.black, 0.06),
          borderColor: 'primary.main',
        },
        '&:focus-visible': {
          outline: 'none',
          borderColor: 'primary.main',
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
      })}
    >
      <SearchIcon sx={{ fontSize: iconSizes.md, color: 'text.secondary' }} />
      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 400 }}>
        {t('consumer.search.placeholder')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', ml: 'auto' }}>
        <Box
          component="kbd"
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            height: 20,
            px: '5px',
            fontSize: fontSizes['2xs'],
            fontFamily: 'inherit',
            fontWeight: 600,
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.common.black, 0.06),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 0.5,
          })}
        >
          {isMac ? '\u2318' : 'Ctrl'}
        </Box>
        <Box
          component="kbd"
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            height: 20,
            px: '5px',
            fontSize: fontSizes['2xs'],
            fontFamily: 'inherit',
            fontWeight: 600,
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.common.black, 0.06),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 0.5,
          })}
        >
          K
        </Box>
      </Box>
    </Box>
  );
}

export default SearchButton;
